/**
 * Package G / G-AC-11 - when opaque accessRevision changes, clear the customer
 * request-failure cache (SoT analogous to admin apiDenyCache) so the next
 * request hits the server (no stale skip). Peer tabs sync via BroadcastChannel
 * + storage (CrossTabBus).
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { clearAllRequestFailures } from '../../shared/api/requestFailureCache.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import {
  customerAccessRevisionBus,
  publishCustomerAccessRevisionChanged,
} from '../../shared/crossTab/customerCrossTab.js';

export function useCustomerAccessRevisionInvalidation(
  tenantCode: string,
  accessRevision: string | undefined,
): void {
  const queryClient = useQueryClient();
  const lastSeenRef = useRef<string | null>(null);

  useEffect(() => {
    return customerAccessRevisionBus.subscribe((message) => {
      if (message.type !== 'access-revision-changed') {
        return;
      }
      if (message.tenantCode !== tenantCode) {
        return;
      }
      clearAllRequestFailures();
      void queryClient.invalidateQueries({
        queryKey: customerQueryKeys.surfaceEntitlement(tenantCode),
      });
      lastSeenRef.current = message.accessRevision;
    });
  }, [queryClient, tenantCode]);

  useEffect(() => {
    if (accessRevision === undefined) {
      return;
    }
    const previous = lastSeenRef.current;
    if (previous === null) {
      lastSeenRef.current = accessRevision;
      return;
    }
    if (previous === accessRevision) {
      return;
    }
    lastSeenRef.current = accessRevision;
    clearAllRequestFailures();
    publishCustomerAccessRevisionChanged(tenantCode, accessRevision);
  }, [accessRevision, tenantCode]);
}